<template>
  <section id="videos" data-qa="video-instructions-section" class="instructions-section">
    <div class="section-header">
      <h2>{{ i18n.t('/video instructions/title') }}</h2>
      <p>{{ i18n.t('/video instructions/introduction') }}</p>
    </div>
    <loader v-if="isLoading" />
    <div v-else data-qa="video-grid" class="video-grid">
      <div
        v-for="(video, index) in videos"
        :key="index"
        data-qa="video-card"
        class="video-card"
        :class="{ 'video-card--playing': activeIndex === index }"
      >
        <div
          data-qa="video-card-play"
          class="video-card__embed"
          @click="activeIndex !== index && activate(index)"
        >
          <!-- Live player -->
          <div
            v-if="activeIndex === index"
            :ref="`player-${index}`"
            data-qa="video-card-player"
            class="video-card__player-container"
          />

          <!-- Poster / play overlay -->
          <template v-else>
            <img
              v-if="thumbnails[index]"
              :src="thumbnails[index]"
              class="video-card__poster"
              :alt="video.title"
            />
            <div v-else class="video-card__poster-placeholder" />
            <div class="video-card__play-btn" aria-label="Afspelen">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </template>

          <!-- Fullscreen button (only while playing) -->
          <button
            v-if="activeIndex === index"
            data-qa="video-card-fullscreen-button"
            class="video-card__fullscreen-btn"
            aria-label="Volledig scherm"
            @click.stop="goFullscreen(index)"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
              />
            </svg>
          </button>
        </div>

        <div class="video-card__footer">
          <span class="video-card__index">{{ index + 1 }}</span>
          <span data-qa="video-card-title" class="video-card__title" :title="video.title">{{
            video.title
          }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import VideoInstructions_Api from '@/api/server/InstructionalVideos.api.js'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'

export default {
  components: { Loader },
  emits: ['onError'],
  data() {
    return {
      videos: [],
      thumbnails: [],
      isLoading: false,
      activeIndex: null,
      players: {},
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  async mounted() {
    await this.loadVimeoSdk()
    this.isLoading = true
    try {
      this.videos = await VideoInstructions_Api.getInstructionalVideos()
      this.thumbnails = new Array(this.videos.length).fill(null)
      this.videos.forEach((video, i) => this.loadThumbnail(video.videoUrl, i))
    } catch {
      this.$emit('onError')
    } finally {
      this.isLoading = false
    }
  },
  beforeUnmount() {
    Object.values(this.players).forEach((p) => p.destroy?.())
  },
  methods: {
    loadVimeoSdk() {
      if (window.Vimeo) return Promise.resolve()
      return new Promise((resolve) => {
        const script = document.createElement('script')
        script.src = 'https://player.vimeo.com/api/player.js'
        script.onload = resolve
        document.head.appendChild(script)
      })
    },
    async activate(index) {
      this.activeIndex = index
      await this.$nextTick()
      const container = this.$refs[`player-${index}`]
      const el = Array.isArray(container) ? container[0] : container
      if (!el || !window.Vimeo) return
      const player = new window.Vimeo.Player(el, {
        url: this.videos[index].videoUrl,
        autoplay: true,
        title: false,
        byline: false,
        portrait: false,
        responsive: true,
      })
      this.players[index] = player
    },
    async goFullscreen(index) {
      const player = this.players[index]
      if (!player) return
      try {
        await player.requestFullscreen()
      } catch {
        // fallback: try on the iframe element directly
        const container = this.$refs[`player-${index}`]
        const el = Array.isArray(container) ? container[0] : container
        const iframe = el?.querySelector('iframe')
        if (iframe) {
          const fn =
            iframe.requestFullscreen ||
            iframe.webkitRequestFullscreen ||
            iframe.mozRequestFullScreen
          fn?.call(iframe)
        }
      }
    },
    async loadThumbnail(videoUrl, index) {
      if (!videoUrl) return
      try {
        const res = await fetch(
          `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}&width=640`
        )
        if (!res.ok) return
        const data = await res.json()
        if (data.thumbnail_url) {
          const updated = [...this.thumbnails]
          updated[index] = data.thumbnail_url
          this.thumbnails = updated
        }
      } catch {
        // fails silently, placeholder shown
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.section-header {
  margin-bottom: 1.5rem;

  h2 {
    margin: 0 0 0.35rem;
  }

  p {
    margin: 0;
    color: $gray-dark;
    font-size: 0.9rem;
  }
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
}

.video-card {
  background: $white;
  border: 1px solid $gray-light;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  &--playing {
    cursor: default;
    transform: none;

    &:hover {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }
  }

  &__embed {
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    background: $gray-darker;

    .video-card__poster,
    .video-card__poster-placeholder,
    .video-card__play-btn,
    .video-card__player-container {
      position: absolute;
      inset: 0;
    }
  }

  &__player-container {
    width: 100%;
    height: 100%;

    :deep(iframe) {
      width: 100% !important;
      height: 100% !important;
    }
  }

  &__poster {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__poster-placeholder {
    background: linear-gradient(135deg, $gray-darker 0%, $gray-dark 100%);
  }

  &__play-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.35);
    transition: background 0.2s ease;

    svg {
      width: 52px;
      height: 52px;
      color: $white;
      filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.5));
      transition: transform 0.15s ease;
    }

    .video-card:hover & {
      background: rgba(0, 0, 0, 0.5);

      svg {
        transform: scale(1.1);
      }
    }
  }

  &__fullscreen-btn {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    width: 2rem;
    height: 2rem;
    border: none;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.55);
    color: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: background 0.15s ease;

    svg {
      width: 1.1rem;
      height: 1.1rem;
    }

    &:hover {
      background: rgba(0, 0, 0, 0.85);
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.6rem 0.875rem;
    border-top: 1px solid $gray-lighter;
  }

  &__index {
    flex-shrink: 0;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 50%;
    background: $color-primary;
    color: $white;
    font-size: 0.7rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.85;
  }

  &__title {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-darker;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.35;
  }
}
</style>
